import {memo, ReactNode, useCallback, useEffect} from 'react';
import React from 'react';
import {useRPC} from '../../hooks/rpc';
import {CONTROL_TYPE_ID_CONTACTOR, ControlModel, ControlTypeModel, IOControlParameters} from '../../types/control';
import {Button} from '../Button';
import {controlSendErrorAction, entityUpdatedAction} from '../../store/control/actions';
import {useDispatch, useSelector} from 'react-redux';
import {useDebounce} from '../../hooks/debounce';
import {Column} from '../Column';
import {Widget} from '../Widget';
import {delay} from '../../utils/promise';
import {useTimer} from '../../hooks/timer';
import {useQuestion} from "../../hooks/question";
import {useLocalization} from "../../hooks/localization";
import {useMessage} from '../../hooks/message';
import { useApproving } from '../../hooks/approving';
import { passwordCheckingTimeoutAction } from '../../store/ui/actions';

type Props = {
    control: ControlModel;
    controlType: ControlTypeModel;
    children?: ReactNode;
};

export const SimpleControl = memo(function SimpleControl({control, controlType, children}: Props) {
    const [rpc] = useRPC();
    const {start, end, isTimedOut} = useTimer(2000);
    const question = useQuestion();
    const {t} = useLocalization();
    const dispatch = useDispatch();
    const {failure} = useMessage()
    const approving = useApproving()
    const passwordTimeoutSycle = useSelector(state => state.ui.passwordTimeoutSycle)


    const updateControl = useDebounce(() => {
        dispatch(entityUpdatedAction({...control, isUpdating: true}, 'control'));
        Promise.race(
            [
                rpc.updateControlValue(control.id, control.current_value === 1 ? 0 : 1),
                delay(2000).then(() => false),
            ],
        ).then(result => {
            if (!result) {
                // todo is necessary?
                dispatch(entityUpdatedAction({...control, isUpdating: false}, 'control'));
            }
        }).catch(failure);

        start();
    }, 200);

    const openAreYouSure = useCallback(() => {
        question(t('ARE_YOU_SURE'), () => {
            updateControl()
        });
    }, [question, t, updateControl]);

    const onClick = useCallback(() => {
        if (controlType.render_type === 'pulse' && control.current_value !== (control.parameters as IOControlParameters)?.default_value) {
            return;
        }
        if (control?.parameters?.["password_control"] && !passwordTimeoutSycle) {
            return approving(() => {
                dispatch(passwordCheckingTimeoutAction(true))
                updateControl()
            })
        }
        control.type_id === CONTROL_TYPE_ID_CONTACTOR ? openAreYouSure() :  updateControl();
    }, [control, controlType, dispatch, updateControl, openAreYouSure, approving, passwordTimeoutSycle]);

    useEffect(() => {
        if (control.hasError) {
            const timeout = setTimeout(() => dispatch(controlSendErrorAction(control.id, false)), 3000);
            return () => clearTimeout(timeout);
        }
    }, [dispatch, control]);

    useEffect(() => {
        if (!control.isUpdating) {
            end();
        }

    }, [end, control.isUpdating]);

    useEffect(() => {
        if (isTimedOut && control.isUpdating) {
            dispatch(entityUpdatedAction({...control, isUpdating: false}, 'control'));
        }
    }, [control, dispatch, isTimedOut]);

    return (
        <Column min={3} max={6}>
            <Widget style={{padding: 0}}>
                <Button
                    onClick={onClick}
                    title={control.name}
                    isBusy={control.isUpdating}
                    hasError={control.hasError}
                    icon={control.current_value ? controlType.image_set.active : controlType.image_set.passive}>
                    {children}
                </Button>
            </Widget>
        </Column>
    );
});
